import { useCallback, useEffect, useRef, useState } from 'react'

import { API } from '@/api'
import { Machine } from '@/models/machine'
import { Snapshot, SnapStatus } from '@/models/snapshot'
import { buildBaseResourceDesc } from '@/pages/create-instance/utils/extraStorage'
import { useTeamStore, useTeamUserRole } from '@/stores/team'
import { fetchAllPages } from '@/utils/pagination'

export interface EnhancedSnapshot extends Snapshot {
  isTeamSnapshot?: boolean
  creatorName?: string
  creatorRole?: number
  creatorResID?: string
}

export function useTeamSnapshots(machine?: Machine, count: number = 1) {
  const [snapshots, setSnapshots] = useState<EnhancedSnapshot[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [domains, setDomains] = useState<number[]>([])
  const requestSeqRef = useRef(0)

  const teamResID = useTeamStore((state) => state.teamTokenData?.teamResID)
  const userRole = useTeamUserRole()

  // Fetch all domains on mount
  useEffect(() => {
    const fetchDomains = async () => {
      try {
        const res = await API.getDomainList()
        if (res.code === 0) {
          setDomains(res.domainInfos?.map((d) => d.domain) || [])
        }
      } catch (error) {
        console.error('Failed to fetch domains:', error)
      }
    }
    fetchDomains()
  }, [])

  const loadSnapshots = useCallback(async () => {
    // Only load when machine is selected
    if (!machine || !teamResID || domains.length === 0) {
      requestSeqRef.current += 1
      setSnapshots([])
      return
    }

    const requestSeq = requestSeqRef.current + 1
    requestSeqRef.current = requestSeq
    setLoading(true)
    setError(null)
    setSnapshots([])

    try {
      const sharedSnapshots = await fetchAllPages({
        fetchPage: (page, pageSize) =>
          API.getTeamSharedSnapshots({
            res_id: teamResID,
            page,
            per_page: pageSize,
            statuses: [SnapStatus.Finished],
            regions: domains,
            resource_desc: buildBaseResourceDesc(machine, count),
          }),
        getItems: (response) => response.snapshots,
      })

      if (requestSeq !== requestSeqRef.current) return

      const enhancedSnapshots: EnhancedSnapshot[] = sharedSnapshots.map((item) => {
        const snapshot = new Snapshot(item.snapshot)
        // Create enhanced snapshot by copying all properties
        const enhanced = Object.assign(Object.create(Object.getPrototypeOf(snapshot)), snapshot, {
          isTeamSnapshot: true,
          creatorName: item.user.user.name,
          creatorRole: item.user.user.role,
          creatorResID: item.user.resID,
        })
        return enhanced as EnhancedSnapshot
      })
      setSnapshots(enhancedSnapshots)
    } catch (err) {
      if (requestSeq !== requestSeqRef.current) return
      console.error('Failed to load team snapshots:', err)
      setError(err as Error)
    } finally {
      if (requestSeq === requestSeqRef.current) {
        setLoading(false)
      }
    }
  }, [machine, teamResID, userRole, domains, count])

  useEffect(() => {
    loadSnapshots()
  }, [loadSnapshots])

  return { snapshots, loading, error, reload: loadSnapshots }
}
